import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { isRequestActive, loadAndTrack } from "redux-request-loading";

import { ApplicationState } from "../../applicationState";

import * as selectors from "./selectors";
import { CustomerDetailModelInRedux, CustomerInRedux, CustomerListModel } from "./model";
import { LOAD_CUSTOMERS, LOAD_CUSTOMER_DETAIL } from "./actiontypes";
import { customerApi } from "./customerApi";
import { loadCustomerDetailSuccess } from "./actions";

type useCustomerDetailsModelReturn =
    { customer: CustomerDetailModelInRedux; isLoading: false; isError: false; refresh: () => void; isLoaded: true }
    | { customer: Exclude<CustomerInRedux, CustomerDetailModelInRedux> | undefined; isLoading: boolean; isError: boolean; refresh: () => void; isLoaded: false }; // eslint-disable-line max-len

/*
* This hook is used to load the customer details from the server using whichever
* combination of customerId from the route or passed in if not present on the url.
* If you don't need the customer details, you can pass `enable: false` to the hook to disable the loading of the customer details
* until you need it by calling `refresh()`
*/
export const useCustomerDetailsModel = (
    { customerId: passedCustomerId }: { customerId?: string } = {},
    { enable }: { enable?: boolean } = {}
): useCustomerDetailsModelReturn => {
    const customerIdFromRoute = useSelector(selectors.routeIdSelector);
    if (passedCustomerId === undefined && customerIdFromRoute === undefined) {
        throw new Error("useCustomerListModel & useCustomerDetailsModel can only be used on a route containing a `customerId` param "
        + "or with a `customerId` param passed to the hook.");
    }
    const dispatch = useDispatch();
    const customers = useSelector(selectors.customersSelector);
    const customerId = passedCustomerId ?? customerIdFromRoute;
    const customer = customers.find(c => c.id === customerId) as CustomerInRedux | undefined;
    const requestKey = `${LOAD_CUSTOMER_DETAIL}_${customerId}`;
    const isLoading = useSelector<ApplicationState, boolean>(state => isRequestActive(state, requestKey));
    const [isError, setIsError] = React.useState(false);
    const refresh = React.useCallback(async () => {
        try {
            // TODO: Replace this with Redux Toolkit
            // @ts-expect-error TS2345
            await loadAndTrack(dispatch, requestKey, (async () => {
                const result = await customerApi.detail(customerId);
                dispatch(loadCustomerDetailSuccess(result));
            })());
            setIsError(false);
        } catch (e) {
            setIsError(true);
            throw e;
        }
    }, [customerId, dispatch, requestKey]);
    React.useEffect(() => {
        if (enable === false
            || isLoading
            || isError
            || (customer && "detailUpdated" in customer && customer.detailUpdated.isSameOrAfter(customer.listUpdated))) {
            return;
        }

        refresh();
    }, [enable, isLoading, customer, refresh, isError]);

    if (customer && "detailUpdated" in customer) {
        return { customer, isLoading: false, isError: false, refresh, isLoaded: true };
    } else {
        return { customer, isLoading, isError, refresh, isLoaded: false };
    }
};

type useCustomerListModelReturn =
    { customer: CustomerListModel | CustomerDetailModelInRedux; isLoading: false; isError: false; refresh: () => void; isLoaded: true }
    | { customer: Exclude<CustomerInRedux, CustomerListModel | CustomerDetailModelInRedux> | undefined; isLoading: boolean; isError: boolean; refresh: () => void; isLoaded: false }; // eslint-disable-line max-len

/*
* This hook is used to read customer list model from the store server using whichever
* combination of customerId from the route or passed in if not present on the url.
* If the customer is not in the store then its details will be loaded.
* If you don't need the customer model straight away then you can pass `enable: false` to the hook to disable the loading of the customer details
* until you need it by calling `refresh()`
*/
export const useCustomerListModel = (
    { customerId: passedCustomerId }: { customerId?: string } = {},
    { enable }: { enable?: boolean } = {}
): useCustomerListModelReturn => {
    const { customer, refresh, isLoading: isLoadingDetails, isError } = useCustomerDetailsModel({ customerId: passedCustomerId }, { enable: false });
    const requestKey = LOAD_CUSTOMERS;
    const isLoadingList = useSelector<ApplicationState, boolean>(state => isRequestActive(state, requestKey));
    React.useEffect(() => {
        if (enable === false || isLoadingList || (customer && "listUpdated" in customer)) {
            return;
        }

        refresh();
    }, [enable, isLoadingList, customer, refresh]);

    if (customer && "listUpdated" in customer) {
        return { customer, isLoading: false, isError: false, refresh, isLoaded: true };
    } else {
        return { customer, isLoading: isLoadingDetails || isLoadingList, isError, refresh, isLoaded: false };
    }
};
